import * as React from "react";
import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useDemandHistory } from "@/api/demandForecast";
import { bucketToTarget } from "@/components/demand-chart/aggregation";
import { ChartCanvas } from "@/components/demand-chart/ChartCanvas";
import { ChartToolbar } from "@/components/demand-chart/ChartToolbar";
import { OverviewBrush } from "@/components/demand-chart/OverviewBrush";
import { baselinePoints, trendPoints } from "@/components/demand-chart/series";
import type {
  Aggregation,
  Point,
  ViewRange,
} from "@/components/demand-chart/types";
import { useDemandChartView } from "@/components/demand-chart/useDemandChartView";
import { Card, CardContent } from "@/components/ui/card";
import { parseDate } from "@/utils/formatters";

import { TotalsInViewCard } from "./cards/TotalsInViewCard";
import { AcrossPill } from "./pills/AcrossPill";
import { ForPill } from "./pills/ForPill";
import type { ForSelection } from "./pills/ForPill";
import { InPill } from "./pills/InPill";
import { SplitPill } from "./pills/SplitPill";
import type { SplitBy } from "./pills/SplitPill";

const SEGMENT_PALETTE = [
  "#4f46e5",
  "#f97316",
  "#10b981",
  "#ef4444",
  "#a855f7",
  "#0ea5e9",
];
const YEAR_MS = 365 * 24 * 60 * 60 * 1000;
const PERIOD_DAYS_BY_AGG: Record<Aggregation, number> = {
  week: 7,
  month: 30,
  quarter: 90,
};
const TREND_WINDOW_DAYS = 120;

export default function DemandExplorer() {
  const { t } = useTranslation();

  // Scope state — controlled by the For / In / Across pills.
  const [forSel, setForSel] = React.useState<ForSelection>({
    items: [],
    labels: [],
  });
  const [locationIds, setLocationIds] = useState<number[]>([]);
  const [segmentIds, setSegmentIds] = useState<number[]>([]);
  const [splitBy, setSplitBy] = useState<SplitBy>("none");

  // Toolbar / view state (shared hook). Explorer surfaces the metric toggle.
  const view = useDemandChartView();
  const { aggregation, metric, mode, activePreset } = view;
  const periodDays = PERIOD_DAYS_BY_AGG[aggregation];

  const query = useDemandHistory({
    items: forSel.items,
    labels: forSel.labels,
    locations: locationIds,
    segments: segmentIds,
    splitBy,
    mode,
  });

  const dataSpan = useMemo<ViewRange>(() => {
    const groups = query.data?.groups ?? [];
    const dates = groups.flatMap((g) =>
      g.points.map((p) => parseDate(p.date).getTime()),
    );
    if (dates.length === 0) {
      const now = Date.now();
      return { min: now - YEAR_MS, max: now };
    }
    return { min: Math.min(...dates), max: Math.max(...dates) };
  }, [query.data]);

  const visible = view.resolveView(dataSpan, dataSpan.max);

  const chartData = useMemo(() => {
    const groups = query.data?.groups ?? [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const datasets: any[] = [];
    groups.forEach((g, i) => {
      const buckets = g.points.map((p) => ({
        date: p.date,
        quantity: p.quantity,
        revenue: p.revenue,
      }));
      const color = SEGMENT_PALETTE[i % SEGMENT_PALETTE.length];

      // Rough per-period baseline — gray in Sum mode, faded segment color in Split.
      datasets.push({
        label: `${g.label} (baseline)`,
        data: baselinePoints(buckets, metric, periodDays),
        borderColor: splitBy === "none" ? "#d4d4d8" : `${color}55`,
        borderWidth: 1,
        pointRadius: 0,
        tension: 0,
      });

      // Gaussian-smoothed trend line.
      datasets.push({
        label: g.label,
        data: trendPoints(buckets, metric, periodDays, TREND_WINDOW_DAYS),
        borderColor: splitBy === "none" ? "#f27a61" : color,
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0.3,
      });
    });
    return { datasets };
  }, [query.data, metric, periodDays, splitBy]);

  // Daily merged history for totals + the nav sparkline.
  const dailyHistory = useMemo<Point[]>(() => {
    const merged = new Map<string, number>();
    for (const g of query.data?.groups ?? []) {
      for (const p of g.points) {
        merged.set(
          p.date,
          (merged.get(p.date) ?? 0) +
            (metric === "revenue" ? p.revenue : p.quantity),
        );
      }
    }
    return Array.from(merged.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([x, y]) => ({ x, y }));
  }, [query.data, metric]);

  const navHistory = useMemo(
    () => bucketToTarget(dailyHistory),
    [dailyHistory],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 py-2 text-sm">
        <span className="text-muted-foreground">
          {t("demand.explorer.connectorFor")}
        </span>
        <ForPill value={forSel} onChange={setForSel} />
        <span className="text-muted-foreground">
          {t("demand.explorer.connectorIn")}
        </span>
        <InPill value={locationIds} onChange={setLocationIds} />
        <span className="text-muted-foreground">
          {t("demand.explorer.connectorAcross")}
        </span>
        <AcrossPill value={segmentIds} onChange={setSegmentIds} />
        <span className="text-muted-foreground">·</span>
        <SplitPill value={splitBy} onChange={setSplitBy} />
      </div>

      <div className="grid grid-cols-[1fr_240px] gap-4">
        <Card>
          <CardContent className="flex flex-col gap-3">
            <ChartCanvas
              data={chartData}
              dataSpan={dataSpan}
              viewRange={visible}
              aggregation={aggregation}
              metric={metric}
              dailyHistory={dailyHistory}
              isLoading={query.isLoading}
              onUserRange={view.handleUserRange}
            />
            <OverviewBrush
              series={navHistory}
              viewRange={visible}
              rangeMax={dataSpan.max}
              onViewRangeChange={(r) => {
                view.setActivePreset(null);
                view.setViewRange(r);
              }}
              onReset={view.reset}
            />
            <div className="mt-1 border-t pt-3">
              <ChartToolbar
                activePreset={activePreset}
                onPreset={view.handlePreset}
                aggregation={aggregation}
                onAggregation={view.setAggregation}
                metric={metric}
                onMetric={view.setMetric}
                mode={mode}
                onMode={view.setMode}
              />
            </div>
            {(query.data?.groups.length ?? 0) > 1 && (
              <div className="text-muted-foreground flex flex-wrap gap-3 text-xs">
                {query.data!.groups.map((g, i) => (
                  <div key={g.key} className="flex items-center gap-1.5">
                    <span
                      className="inline-block size-2 rounded-full"
                      style={{
                        backgroundColor:
                          SEGMENT_PALETTE[i % SEGMENT_PALETTE.length],
                      }}
                    />
                    {g.label}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <TotalsInViewCard
            groups={query.data?.groups ?? []}
            viewRange={visible}
          />
        </div>
      </div>
    </div>
  );
}
